import React, { useContext, useReducer, useState } from 'react';

import { Box, Stack, TextField } from '@mui/material';

import { LoadingButton } from '../../../Loaders';

import { object, ref, string, ValidationError } from 'yup';

import Axios from '../../../../Assets/Axios/Axios';

import ToastContext from '../../../../Context/ToastContext';

import { useFormContext } from '../FormContext';

import PasswordField from '../../../PasswordField';

import { CreateUser } from '../../../../Server/Mutation';

import Firebase from './../../../../lib/firebase';

const PersonalInformation = () => {
  const { inputs, closeModal } = useFormContext();
  const { showToastMessage } = useContext(ToastContext);

  const [isSignupDisabled, setIsSignupDisabled] = useState(false);
  const [error, updateErrors] = useReducer(
    errorsReducer,
    undefined,
    initialFormErrors
  );
  const [formFields, updateFormFields] = useReducer(
    (state, field) => ({ ...state, ...field }),
    undefined,
    initialFormFields
  );

  function handleChange(event) {
    const { name, value } = event.target;
    updateFormFields({ [name]: value });
  }

  async function handleSubmit(event) {
    try {
      event.preventDefault();
      updateErrors({ type: ERROR_ACTIONS.CLEAR });
      setIsSignupDisabled(true);

      const validatedData = await schema.validate(formFields, {
        abortEarly: false,
      });

      //firebase user creation
      const response = await Firebase.createNewUser({
        email: inputs.email,
        password: validatedData.password,
      });

      if (response?.errors)
        switch (response.errors) {
          case 'auth/email-already-in-use':
            showToastMessage('Please use a different email to signup', 'info');
            return undefined;
          default:
            throw new Error();
        }

      //DB user creation
      const { errors } = await Axios(CreateUser({
          ...validatedData,
          email: inputs.email,
          mobileNumber: inputs.mobileNumber,
          fbUID: response.data.uid,
          postalCode: validatedData.postCode,
        }));

      if (errors) throw new Error();

      showToastMessage('Signup Successful', 'success');
      closeModal();
    } catch (error) {
      if (error instanceof ValidationError) {
        const errors = error.inner.reduce(
          (errs, err) => ({ ...errs, [err.path]: err.message }),
          {}
        );
        updateErrors({ type: ERROR_ACTIONS.SET, payload: errors });
        return undefined;
      }
      showToastMessage('Something went wrong while trying to add the account');
    } finally {
      setIsSignupDisabled(false);
    }
  }

  return (
    <Stack
      component='form'
      alignItems='center'
      gap={4}
      px={{ md: 10 }}
      pb={{ md: 16 }}
      pt={{ xs: 2, md: 4 }}
      onSubmit={handleSubmit}
    >
      <Box
        display='grid'
        gridTemplateColumns={{ xs: 'auto', sm: '1fr 1fr' }}
        gap={2}
      >
        <TextField
          inputProps={{ spellCheck: false }}
          placeholder='First Name'
          name='firstName'
          onChange={handleChange}
          value={formFields.firstName}
          helperText={error.firstName}
          error={Boolean(error.firstName)}
        />
        <TextField
          inputProps={{ spellCheck: false }}
          placeholder='Last Name'
          name='lastName'
          onChange={handleChange}
          value={formFields.lastName}
          helperText={error.lastName}
          error={Boolean(error.lastName)}
        />
        <TextField
          InputProps={{ readOnly: true }}
          value={inputs.email}
          disabled
        />

        <TextField
          InputProps={{ readOnly: true }}
          value={inputs.mobileNumber}
          disabled
        />

        <TextField
          inputProps={{ spellCheck: false }}
          autoComplete='address-line1'
          placeholder='Address'
          name='addressLine1'
          onChange={handleChange}
          value={formFields.addressLine1}
          helperText={error.addressLine1}
          error={Boolean(error.addressLine1)}
        />

        <TextField
          inputProps={{ spellCheck: false }}
          autoComplete='address-level1'
          placeholder='Province'
          name='province'
          onChange={handleChange}
          value={formFields.province}
          helperText={error.province}
          error={Boolean(error.province)}
        />

        <TextField
          inputProps={{ spellCheck: false }}
          autoComplete='postal-code'
          placeholder='Postal Code'
          name='postCode'
          onChange={handleChange}
          value={formFields.postCode}
          helperText={error.postCode}
          error={Boolean(error.postCode)}
        />

        <PasswordField
          autoComplete='new-password'
          placeholder='Password'
          name='password'
          onChange={handleChange}
          value={formFields.password}
          helperText={error.password}
          error={Boolean(error.password)}
        />

        <PasswordField
          autoComplete='new-password'
          placeholder='Re-enter Password'
          name='confirmPassword'
          onChange={handleChange}
          value={formFields.confirmPassword}
          helperText={error.confirmPassword}
          error={Boolean(error.confirmPassword)}
        />
      </Box>

      <LoadingButton
        color='blue'
        type='submit'
        isLoading={isSignupDisabled}
        sx={{ width: 3 / 5, mx: 'auto' }}
      >
        Signup
      </LoadingButton>
    </Stack>
  );
};

const formFields = [
  'firstName',
  'lastName',
  'addressLine1',
  'postCode',
  'province',
  'password',
  'confirmPassword',
];

function initialFormFields() {
  return formFields.reduce(
    (acc, fieldName) => ({ ...acc, [fieldName]: '' }),
    {}
  );
}

function initialFormErrors() {
  return formFields.reduce(
    (acc, fieldName) => ({ ...acc, [fieldName]: null }),
    {}
  );
}

const ERROR_ACTIONS = Object.freeze({
  SET: 'SET',
  CLEAR: 'CLEAR',
});

function errorsReducer(state, action) {
  switch (action.type) {
    case ERROR_ACTIONS.SET:
      return { ...state, ...action.payload };
    case ERROR_ACTIONS.CLEAR:
      return Object.keys(state).reduce((errors, key) => {
        errors[key] = null;
        return errors;
      }, {});
    //no default
  }
}

const schema = object({
  firstName: string().trim().required().label('First Name'),
  lastName: string().trim().required().label('Last Name'),
  addressLine1: string().trim().required().label('Address Line 1'),
  postCode: string().trim().required().label('Postal Code'),
  province: string().trim().required().label('Province'),
  password: string()
    .required()
    .min(6, 'Password must be at least 6 characters')
    .label('Password'),
  confirmPassword: string().oneOf([ref('password')], 'Passwords do not match'),
});

export default PersonalInformation;
